import { useQuery } from "@tanstack/react-query";
import { Loader2, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  extractAIContext,
  formatOverrideLabel,
  resolveContextSnapshot,
} from "@/lib/composerSnapshot";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  /** Raw `context_snapshot` jsonb value from a plan / draft / publication row. */
  contextSnapshot: unknown;
  /** Label for the row this snapshot belongs to, e.g. "Planning pass · 2 hours ago". */
  runLabel: string;
};

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="grid grid-cols-[140px_1fr] items-start gap-3 py-2 text-sm">
    <span className="text-xs uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
    <div className="text-foreground">{value}</div>
  </div>
);

export const ContextSnapshotViewer = ({
  open,
  onOpenChange,
  workspaceId,
  contextSnapshot,
  runLabel,
}: Props) => {
  const ai = extractAIContext(contextSnapshot);

  const query = useQuery({
    queryKey: ["context-snapshot-resolve", workspaceId, ai],
    queryFn: () => resolveContextSnapshot(workspaceId, ai),
    enabled: open && Boolean(workspaceId),
    staleTime: 60_000,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-md overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Context used
          </SheetTitle>
          <SheetDescription>{runLabel}</SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {query.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Resolving snapshot…
            </div>
          ) : !ai || query.data?.isEmpty ? (
            <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
              No context recorded for this run. (Older runs from before context wiring don't have a snapshot.)
            </div>
          ) : (
            <div className="divide-y divide-border">
              <Row
                label="Voice profile"
                value={
                  query.data?.voiceLabel ?? (
                    <span className="text-muted-foreground">— none —</span>
                  )
                }
              />
              <Row
                label="Avatar"
                value={
                  query.data?.avatarLabel ?? (
                    <span className="text-muted-foreground">— none —</span>
                  )
                }
              />
              <Row
                label="Writing samples"
                value={
                  query.data?.sampleLabels.length ? (
                    <div className="flex flex-wrap gap-1">
                      {query.data.sampleLabels.map((label) => (
                        <Badge key={label} variant="secondary">
                          {label}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">— none —</span>
                  )
                }
              />
              <Row
                label="Framework"
                value={
                  query.data?.frameworkPresent ? (
                    <Badge variant="secondary">Active framework included</Badge>
                  ) : (
                    <span className="text-muted-foreground">Not included</span>
                  )
                }
              />
              <Row
                label="Source-of-truth"
                value={
                  query.data?.sourceOfTruthLabels.length ? (
                    <ul className="space-y-1">
                      {query.data.sourceOfTruthLabels.map((title) => (
                        <li key={title} className="text-sm">
                          • {title}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-muted-foreground">— none —</span>
                  )
                }
              />
              <Row
                label="Overrides applied"
                value={
                  query.data?.overridesApplied.length ? (
                    <div className="flex flex-wrap gap-1">
                      {query.data.overridesApplied.map((key) => (
                        <Badge key={key} variant="outline" className="text-xs">
                          {formatOverrideLabel(key)}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">None</span>
                  )
                }
              />
              <Separator className="my-2" />
              <Row
                label="Model"
                value={
                  query.data?.model ?? <span className="text-muted-foreground">— unknown —</span>
                }
              />
              <Row
                label="Assembled at"
                value={
                  query.data?.assembledAt ? (
                    new Date(query.data.assembledAt).toLocaleString()
                  ) : (
                    <span className="text-muted-foreground">— unknown —</span>
                  )
                }
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
